'use client';

import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { ChevronDownIcon } from 'lucide-react';
import * as React from 'react';


import { Button, buttonVariants } from './button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuGroup,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from './dropdown-menu';

import { cn } from '@/lib/utils';


// Define size configurations
const sizeConfig = {
  xs: {
    height: 'h-[26px]',
    primaryPadding: 'px-3 py-1',
    triggerPadding: 'px-2 py-1',
  },
  sm: {
    height: 'h-8',
    primaryPadding: 'px-3',
    triggerPadding: 'px-2',
  },
  default: {
    height: 'h-9',
    primaryPadding: 'px-4 py-2',
    triggerPadding: 'px-2 py-2',
  },
  lg: {
    height: 'h-10',
    primaryPadding: 'px-6',
    triggerPadding: 'px-3',
  },
  icon: {
    height: 'size-9',
    primaryPadding: '',
    triggerPadding: '',
  },
};

// Context for sharing props between components
interface SplitButtonContextValue {
  variant?: VariantProps<typeof buttonVariants>['variant'];
  size?: VariantProps<typeof buttonVariants>['size'];
  disabled?: boolean;
  hasSecondary?: boolean;
  sizeClasses?: (typeof sizeConfig)[keyof typeof sizeConfig];
}

const SplitButtonContext = React.createContext<SplitButtonContextValue | undefined>(undefined);

const useSplitButtonContext = () => {
  const context = React.useContext(SplitButtonContext);
  if (!context) {
    throw new Error('SplitButton components must be used within a SplitButton');
  }
  return context;
};

// Root component
interface SplitButtonProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof buttonVariants> {
  disabled?: boolean;
}

const SplitButton = React.forwardRef<HTMLDivElement, SplitButtonProps>(
  ({ variant, size = 'default', disabled, children, className, ...props }, ref) => {
    // Check if children includes a SplitButtonSecondary
    const hasSecondary = React.useMemo(() => {
      return React.Children.toArray(children).some(
        (child) => React.isValidElement(child) && child.type === SplitButtonSecondary
      );
    }, [children]);

    const sizeClasses = sizeConfig[size as keyof typeof sizeConfig] || sizeConfig.default;

    const contextValue = React.useMemo(
      () => ({ variant, size, disabled, hasSecondary, sizeClasses }),
      [variant, size, disabled, hasSecondary, sizeClasses]
    );

    return (
      <SplitButtonContext.Provider value={contextValue}>
        <div ref={ref} className={cn('flex items-center w-fit', className)} data-slot="split-button" {...props}>
          {children}
        </div>
      </SplitButtonContext.Provider>
    );
  }
);
SplitButton.displayName = 'SplitButton';

// Primary action component
interface SplitButtonPrimaryProps extends Omit<React.ComponentPropsWithoutRef<typeof Button>, 'size'> {
  asChild?: boolean;
}

const SplitButtonPrimary = React.forwardRef<HTMLButtonElement, SplitButtonPrimaryProps>(
  ({ className, variant, disabled, asChild, ...props }, ref) => {
    const context = useSplitButtonContext();
    const Comp = asChild ? Slot : Button;

    return (
      <Comp
        ref={ref}
        variant={variant ?? context.variant}
        size={context.size}
        disabled={disabled ?? context.disabled}
        className={cn(
          'flex-1',
          context.sizeClasses?.height,
          context.sizeClasses?.primaryPadding,
          context.hasSecondary && 'rounded-r-none border-r-0',
          className
        )}
        data-slot="split-button-primary"
        {...props}
      />
    );
  }
);
SplitButtonPrimary.displayName = 'SplitButtonPrimary';

// Dropdown trigger component
interface SplitButtonTriggerProps extends React.HTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  variant?: VariantProps<typeof buttonVariants>['variant'];
}

const SplitButtonTrigger = React.forwardRef<HTMLButtonElement, SplitButtonTriggerProps>(
  ({ className, variant, asChild, children, ...props }, ref) => {
    const context = useSplitButtonContext();
    const Comp = asChild ? Slot : 'button';

    return (
      <DropdownMenuTrigger asChild>
        <Comp
          ref={ref}
          className={cn(
            buttonVariants({
              variant: variant ?? context.variant,
              size: undefined, // Don't apply size from buttonVariants
            }),
            'rounded-l-none',
            context.sizeClasses?.height,
            context.sizeClasses?.triggerPadding,
            className
          )}
          disabled={context.disabled}
          data-slot="split-button-trigger"
          {...props}
        >
          {children ?? <ChevronDownIcon className="size-4" />}
        </Comp>
      </DropdownMenuTrigger>
    );
  }
);
SplitButtonTrigger.displayName = 'SplitButtonTrigger';

// Secondary actions container
interface SplitButtonSecondaryProps {
  children: React.ReactNode;
  className?: string;
  variant?: VariantProps<typeof buttonVariants>['variant'];
}

const SplitButtonSecondary = React.forwardRef<HTMLDivElement, SplitButtonSecondaryProps>(
  ({ children, className, variant, ...props }, ref) => {
    return (
      <DropdownMenu>
        <SplitButtonTrigger className={className} variant={variant} />
        <DropdownMenuContent ref={ref} align="end" className="min-w-20 w-auto" data-slot="split-button-dropdown" {...props}>
          {children}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }
);
SplitButtonSecondary.displayName = 'SplitButtonSecondary';

// Export all components and reexport dropdown menu items for convenience
export {
  SplitButton,
  SplitButtonPrimary,
  SplitButtonSecondary,
  SplitButtonTrigger,
  // Re-export dropdown menu components for convenience
  DropdownMenuItem as SplitButtonItem,
  DropdownMenuGroup as SplitButtonGroup,
  DropdownMenuSeparator as SplitButtonSeparator,
  DropdownMenuLabel as SplitButtonLabel,
};
